/* eslint-disable perfectionist/sort-objects -- For routes we prefer the order: path, element */

import 'core-js/stable';
import 'regenerator-runtime/runtime';
import './create-nonce';
import isPropValid from '@emotion/is-prop-valid';
import { defaultTheme, ThemeProvider } from '@ksyos/design-system';
import { createBrowserRouter, Outlet, RouterProvider } from '@ksyos/router';
import { TextDecoder } from 'fastestsmallesttextencoderdecoder';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import { StyleSheetManager } from 'styled-components';
import type { FrontendSettings } from '../common/interfaces/frontendSettings';
import { loadGlobalize } from '../common/translate/globalize';
import { App } from './components/App';
import { CheckRequestStatus } from './components/pages/CheckRequestStatus';
import { Contact } from './components/pages/Contact';
import { Cookies } from './components/pages/Cookies';
import { NewAltoDermaOthProcedure } from './components/pages/DermaOth/NewAltoDermaOthProcedure';
import { NewDermaOthProcedure } from './components/pages/DermaOth/NewDermaOthProcedure';
import { GhcInformation } from './components/pages/GHC/GhcInformation';
import { NewGhcRequest } from './components/pages/GHC/NewGhcRequest';
import { HearingLossDownloadRecipe } from './components/pages/HearingLoss/HearingLossDownloadRecipe';
import { Home } from './components/pages/Home';
import { Login } from './components/pages/Login';
import { MraReferral } from './components/pages/OSAS/MraReferral';
import { NewOsasQuestionnaires } from './components/pages/OSAS/NewOsasQuestionnaires';
import { OsasInstructions } from './components/pages/OSAS/OsasInstructions';
import { OsasPatientQuestionnaire } from './components/pages/OSAS/patientQuestionnaire/OsasPatientQuestionnaire';
import { Questionnaire } from './components/pages/OSAS/Questionnaire';
import { OsasFaq } from './components/pages/OsasFaq';
import { LifestyleQuestionnaire } from './components/pages/OsasFlex/components/lifestyleQuestionnaire/LifestyleQuestionnaire';
import { OsasInvoicingInformation } from './components/pages/OsasFlex/components/osasInvoicingInformation/OsasInvoicingInformation';
import { OsasDentistInformation } from './components/pages/OsasFlex/OsasDentistInformation';
import { OsasFaq as OsasFaqFlex } from './components/pages/OsasFlex/OsasFaq';
import { OsasFlexQuestionnaires } from './components/pages/OsasFlex/OsasFlexQuestionnaires';
import { OsasMraHowToUseInformation } from './components/pages/OsasFlex/OsasMraHowToUseInformation';
import { Questionnaire as OsasFlexQuestionnaire } from './components/pages/OsasFlex/Questionnaire';
import { QuestionnairesSuccess } from './components/pages/OsasFlex/QuestionnairesSuccess';
import { OverKsyos } from './components/pages/OverKsyos';
import { Toegankelijkheid } from './components/pages/Toegankelijkheid';
import { YourData } from './components/pages/YourData';
import { store } from './configureStore';
import { configureAndStartDatadogRum } from './utils/datadogRumUtils';

// Fix for Edge error: 'TextEditor' not defined
if (!window['TextDecoder']) {
    window['TextDecoder'] = TextDecoder;
}

const { ksyosSettings } = window as unknown as { ksyosSettings: FrontendSettings };
const { datadogSettings } = ksyosSettings;

// eslint-disable-next-line @typescript-eslint/no-require-imports
const globalizeLocalizer = require('react-widgets-globalize');

loadGlobalize();
globalizeLocalizer();
configureAndStartDatadogRum(datadogSettings);

// This implements the default behavior from styled-components v5
function shouldForwardProp(propName: string, target: unknown) {
    if (typeof target === 'string') {
        // For HTML elements, forward the prop if it is a valid HTML attribute
        return isPropValid(propName);
    }

    // For other elements, forward all props
    return true;
}

const rootNode = document.getElementById('app');
if (rootNode) {
    const root = createRoot(rootNode);
    root.render(
        <>
            {process.env.REACT_ENABLE_STRICTMODE === 'true' ? (
                <StrictMode>
                    <MijnKsyosApplication />
                </StrictMode>
            ) : (
                <MijnKsyosApplication />
            )}
        </>
    );
}

function MijnKsyosApplication() {
    return (
        <ReduxProvider store={store}>
            <StyleSheetManager shouldForwardProp={shouldForwardProp}>
                <ThemeProvider theme={defaultTheme}>
                    <RouterProvider router={createBrowserRouter(routes)} />
                </ThemeProvider>
            </StyleSheetManager>
        </ReduxProvider>
    );
}

const routes = [
    { path: '/saml/login', element: <Login /> },
    { path: 'altoFlow/newRequest', element: <NewAltoDermaOthProcedure /> },
    { path: 'satisfactionQuestionnaires/:patientQuestionnaireId', element: <OsasPatientQuestionnaire /> },
    {
        path: '',
        element: <App />,
        children: [
            {
                index: true,
                path: '/',
                element: <Home />,
            },
            { path: 'hearingLoss/downloadRecipe/:processActivityId', element: <HearingLossDownloadRecipe /> },
            { path: 'osasFaqPage', element: <OsasFaq /> },
            { path: 'osasFaqs', element: <OsasFaqFlex /> },
            { path: 'osas/content', element: <OsasInstructions /> },
            { path: 'osaso/questionnaires', element: <NewOsasQuestionnaires /> },
            { path: 'osasvo/questionnaires', element: <NewOsasQuestionnaires /> },
            { path: 'osasq/questionnaires', element: <NewOsasQuestionnaires /> },
            { path: 'osas/questionnaires/:actionId', element: <OsasFlexQuestionnaire /> },
            { path: 'osas/questionnaires/:questionnaireName/success', element: <QuestionnairesSuccess /> },
            { path: 'osas/lifestyleQuestionnaire/:actionId', element: <LifestyleQuestionnaire /> },
            { path: 'osas/questionnaires', element: <OsasFlexQuestionnaires /> },
            { path: 'yourData', element: <YourData /> },
            {
                path: 'ghc',
                element: <Outlet />,
                children: [
                    { path: 'request', element: <NewGhcRequest /> },
                    { path: 'information', element: <GhcInformation /> },
                ],
            },
            { path: 'dermaOth/request', element: <NewDermaOthProcedure /> },
            { path: 'questionnaire/request', element: <Questionnaire /> },
            { path: 'mraReferral', element: <MraReferral /> },
            { path: 'osas/invoicingInformation', element: <OsasInvoicingInformation /> },
            { path: 'osas/dentistInformation', element: <OsasDentistInformation /> },
            { path: 'osas/mraInformation', element: <OsasMraHowToUseInformation /> },
        ],
    },
    { path: '/:service/newRequest', element: <CheckRequestStatus /> },
    { path: '/contact', element: <Contact /> },
    { path: '/overksyos', element: <OverKsyos /> },
    { path: '/toegankelijkheid', element: <Toegankelijkheid /> },
    { path: '/cookies', element: <Cookies /> },
];
